import React, { Fragment, memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';

import { floatDaysLabel } from '../schedule/scheduleEntries';

import MilestoneDetailModal from './MilestoneDetailModal';
import {
    CATEGORY_ROW_H,
    GAP_W,
    HEADER_GROUP_H,
    HEADER_H,
    HEADER_PERIOD_H,
    LEFT_W,
    SOURCE_ROW_H,
    SUBCATEGORY_ROW_H,
    assignLanes,
    columnRange,
    dateAt,
    layoutColumns,
    measurePill,
    pillTop,
    rowHeight,
    rowIndexAt,
    scrollLeftFor,
    xOf,
} from './roadmapGeometry';

import { formatDateShort } from 'Helpers/dateFormat';
import { modalOptions } from 'Helpers/modalOptions';

import ProjectRoadmapContext, { styleChangesData } from 'Context/project_plan/ProjectRoadmapContext';

import useModal from 'Hooks/useModal';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import StarFilledIcon from 'Assets/icons/star-filled.svg';

// Rows and columns outside the viewport (plus this margin) are not rendered, so a multi-year Days
// view or a large plan stays responsive.
const ROW_OVERSCAN_PX = 400;
const TODAY_VIEWPORT_SHARE = 0.3;
// Scroll settles this long before its position is handed to the settings (which then save on their own debounce).
const SCROLL_PERSIST_MS = 300;
// Room a period label keeps while its column slides under the frozen label column.
const STICKY_PERIOD_LABEL_W = 56;

const HATCH = 'tw-bg-[repeating-linear-gradient(135deg,#C6C6C7_0_2px,transparent_2px_6px)]';
const HATCH_LIGHT = 'tw-bg-[repeating-linear-gradient(135deg,#E2E2E3_0_2px,transparent_2px_6px)]';

const countsLabel = (counts) =>
    `${counts.milestones === 1 ? $t('1 milestone') : $t('{{count}} milestones', { count: counts.milestones })} · ${$t('{{count}} key', { count: counts.key })}`;

const placePills = (milestones, layout, measure) => {
    const items = milestones.map((milestone) => ({ milestone, x: xOf(layout, milestone.date), w: measure(milestone) })).sort((a, b) => a.x - b.x);

    return { items, lanes: assignLanes(items) };
};

/**
 * Flatten the tree into positioned rows. Collapsed sources show their band only; a collapsed
 * category keeps its own milestones but hides its sub-categories.
 */
const buildRows = (sources, collapsed, firstLevel, layout, pillMode, measure) => {
    const rows = [];
    let top = 0;
    let maxRight = 0;

    const push = (row) => {
        rows.push({ ...row, top });
        top += row.height;
        row.pills.forEach((pill) => {
            maxRight = Math.max(maxRight, pill.x + pill.w);
        });
    };

    sources.forEach((source) => {
        const sourceKey = `source:${source.key}`;
        const sourceCollapsed = !!collapsed[sourceKey];

        push({
            key: sourceKey,
            type: 'source',
            name: source.name,
            counts: source.counts,
            collapsible: true,
            collapsed: sourceCollapsed,
            height: SOURCE_ROW_H,
            pills: [],
        });

        if (sourceCollapsed) {
            return;
        }

        source.categories.forEach((category) => {
            const categoryKey = `category:${category.id}`;
            const collapsible = !firstLevel && category.subcategories.length > 0;
            const categoryCollapsed = collapsible && !!collapsed[categoryKey];
            const { items, lanes } = placePills(category.milestones, layout, measure);

            push({
                key: categoryKey,
                type: 'category',
                name: category.name,
                counts: category.counts,
                collapsible,
                collapsed: categoryCollapsed,
                height: rowHeight(lanes, pillMode, CATEGORY_ROW_H),
                pills: items,
            });

            if (categoryCollapsed) {
                return;
            }

            category.subcategories.forEach((subcategory) => {
                const placed = placePills(subcategory.milestones, layout, measure);

                push({
                    key: `subcategory:${subcategory.id}`,
                    type: 'subcategory',
                    name: subcategory.name,
                    height: rowHeight(placed.lanes, pillMode, SUBCATEGORY_ROW_H),
                    pills: placed.items,
                });
            });
        });
    });

    return { rows, height: top, maxRight };
};

const RoadmapCalendar = () => {
    const { data, settings, update, toggleCollapsed, registerScroller, registerPositionFlusher, milestoneUrl } = useContext(ProjectRoadmapContext);

    const scrollerRef = useRef(null);
    const [viewport, setViewport] = useState({ width: 0, height: 0 });
    const [scroll, setScroll] = useState({ left: 0, top: 0 });
    const [fontFamily, setFontFamily] = useState(null);
    const [fontsVersion, setFontsVersion] = useState(0);
    const [tooltip, setTooltip] = useState(null);
    const [selectedMilestone, setSelectedMilestone] = useState(null);

    const milestoneModal = useModal({ options: { ...modalOptions(true), backdrop: 'dynamic' } });
    const { show: showMilestoneModal, hide: hideMilestoneModal } = milestoneModal;

    // The data echoes the settings it was built for, so geometry never mixes a new View by with
    // the previous view's columns while a refetch is in flight.
    const builtFor = data.settings;
    const pillMode = settings.pill;

    // ── Viewport + fonts ──
    useLayoutEffect(() => {
        const element = scrollerRef.current;
        const measure = () => setViewport({ width: Math.max(0, element.clientWidth - LEFT_W), height: Math.max(0, element.clientHeight - HEADER_H) });

        measure();
        setFontFamily(window.getComputedStyle(element).fontFamily);

        const observer = new ResizeObserver(measure);
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        let cancelled = false;

        document.fonts?.ready.then(() => {
            if (!cancelled) {
                setFontsVersion((version) => version + 1);
            }
        });

        return () => {
            cancelled = true;
        };
    }, []);

    // ── Geometry ──
    const layout = useMemo(() => layoutColumns(data.timeline.periods, viewport.width, builtFor.view), [data.timeline, viewport.width, builtFor.view]);

    const measure = useMemo(() => {
        const cache = new Map();

        return (milestone) => {
            // A new critical path target changes the float, and with it the pill's width.
            const key = `${milestone.id}:${milestone.float ?? ''}`;

            if (!cache.has(key)) {
                cache.set(key, measurePill(milestone, pillMode, fontFamily || 'sans-serif'));
            }

            return cache.get(key);
        };
        // fontsVersion re-measures once web fonts finish loading.
    }, [pillMode, fontFamily, fontsVersion]);

    const {
        rows,
        height: bodyHeight,
        maxRight,
    } = useMemo(
        () => buildRows(data.tree.sources, settings.collapsed, builtFor.firstLevel, layout, pillMode, measure),
        [data.tree.sources, settings.collapsed, builtFor.firstLevel, layout, pillMode, measure],
    );

    const contentWidth = LEFT_W + Math.max(layout.width, maxRight + 8);

    const layoutRef = useRef(layout);
    layoutRef.current = layout;
    const dataRef = useRef(data);
    dataRef.current = data;
    const viewportRef = useRef(viewport);
    viewportRef.current = viewport;

    const defaultLeft = useCallback(() => {
        const current = layoutRef.current;
        const today = dataRef.current.today;
        const columns = current.columns;

        if (!columns.length || today < columns[0].start || today >= columns[columns.length - 1].end) {
            return 0;
        }

        return Math.max(0, xOf(current, today) - viewportRef.current.width * TODAY_VIEWPORT_SHARE);
    }, []);

    // ── Anchor restore ──
    // `forData` is the response the anchor was captured against: the restore waits for the next
    // one, so a resize while a refetch is in flight cannot apply it to the old columns.
    const pendingRestoreRef = useRef({ anchor: settings.anchor, scrollTop: settings.scrollTop, forData: null });

    // Where a restore left the scroller. The scroll event it fires is not the user moving, so it
    // must not save a position: a first visit would otherwise store today's default as an anchor.
    const programmaticPositionRef = useRef(null);

    useLayoutEffect(() => {
        const pending = pendingRestoreRef.current;
        const element = scrollerRef.current;

        // Switching from the List can mount on the List's data, whose rows and Hide empty columns
        // can differ; wait for the Calendar's own.
        const builtForList = styleChangesData(builtFor) && builtFor.style !== 'calendar';

        if (!pending || !viewport.width || builtForList || (pending.forData && pending.forData === data)) {
            return;
        }

        element.scrollLeft = pending.anchor ? scrollLeftFor(layout, pending.anchor) : defaultLeft();

        if (pending.scrollTop !== null && pending.scrollTop !== undefined) {
            element.scrollTop = pending.scrollTop;
        }

        pendingRestoreRef.current = null;
        programmaticPositionRef.current = { left: element.scrollLeft, top: element.scrollTop };
        setScroll({ left: element.scrollLeft, top: element.scrollTop });
    }, [data, builtFor, layout, viewport.width, defaultLeft]);

    useEffect(() => {
        registerScroller({
            captureAnchor: () => dateAt(layoutRef.current, scrollerRef.current?.scrollLeft || 0),
            requestRestore: (anchor) => {
                pendingRestoreRef.current = { anchor, scrollTop: null, forData: dataRef.current };
            },
            goToToday: () => {
                const element = scrollerRef.current;
                const today = dataRef.current.today;

                if (element && layoutRef.current.columns.length) {
                    element.scrollTo({
                        left: Math.max(0, xOf(layoutRef.current, today) - viewportRef.current.width * TODAY_VIEWPORT_SHARE),
                        behavior: 'smooth',
                    });
                }
            },
        });

        return () => registerScroller(null);
    }, [registerScroller]);

    // ── Scrolling ──
    const frameRef = useRef(null);
    const persistTimerRef = useRef(null);
    // The latest unsaved position, kept outside the DOM so unmounting can still save it.
    const pendingPositionRef = useRef(null);

    const persistPosition = useCallback(() => {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;

        if (pendingPositionRef.current) {
            update(pendingPositionRef.current);
            pendingPositionRef.current = null;
        }
    }, [update]);

    const handleScroll = useCallback(() => {
        const element = scrollerRef.current;
        setTooltip(null);

        if (!frameRef.current) {
            frameRef.current = window.requestAnimationFrame(() => {
                frameRef.current = null;
                setScroll({ left: element.scrollLeft, top: element.scrollTop });
            });
        }

        const programmatic = programmaticPositionRef.current;

        if (programmatic && Math.abs(element.scrollLeft - programmatic.left) < 1 && Math.abs(element.scrollTop - programmatic.top) < 1) {
            return;
        }

        programmaticPositionRef.current = null;

        pendingPositionRef.current = { anchor: dateAt(layoutRef.current, element.scrollLeft), scrollTop: Math.round(element.scrollTop) };
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = setTimeout(persistPosition, SCROLL_PERSIST_MS);
    }, [persistPosition]);

    const persistPositionRef = useRef(persistPosition);
    persistPositionRef.current = persistPosition;

    useEffect(() => registerPositionFlusher(() => persistPositionRef.current()), [registerPositionFlusher]);

    useEffect(
        () => () => {
            persistPositionRef.current();
            window.cancelAnimationFrame(frameRef.current);
        },
        [],
    );

    // ── Windowing ──
    const windowLeft = scroll.left - viewport.width;
    const windowRight = scroll.left + 2 * viewport.width;
    const [firstColumn, lastColumn] = columnRange(layout, Math.max(0, windowLeft), windowRight);
    const visibleColumns = lastColumn >= firstColumn ? layout.columns.slice(firstColumn, lastColumn + 1) : [];

    const firstRow = rowIndexAt(rows, scroll.top - ROW_OVERSCAN_PX);
    const lastRow = rowIndexAt(rows, scroll.top + viewport.height + ROW_OVERSCAN_PX);
    const visibleRows = rows.slice(firstRow, lastRow + 1);

    const todayX = useMemo(() => {
        const columns = layout.columns;

        if (!columns.length || data.today < columns[0].start || data.today >= columns[columns.length - 1].end) {
            return null;
        }

        return xOf(layout, data.today);
    }, [layout, data.today]);

    // ── Milestone interactions ──
    const openMilestone = useCallback(
        (milestone) => {
            setTooltip(null);
            setSelectedMilestone(milestone);
            showMilestoneModal();
        },
        [showMilestoneModal],
    );

    const showTooltip = useCallback((milestone, rect) => setTooltip({ milestone, left: rect.left, top: rect.bottom + 6 }), []);
    const hideTooltip = useCallback(() => setTooltip(null), []);

    return (
        <Fragment>
            <div
                ref={scrollerRef}
                // The time axis always runs left to right; labels inside keep their own direction.
                dir="ltr"
                onScroll={handleScroll}
                className="tw-relative tw-overflow-auto tw-max-h-[70vh] tw-min-h-[240px] tw-border tw-border-charcoal-25 tw-rounded-lg tw-bg-white"
            >
                <div className="tw-relative" style={{ width: contentWidth }}>
                    <CalendarHeader
                        layout={layout}
                        visibleColumns={visibleColumns}
                        scrollLeft={scroll.left}
                        firstLevel={builtFor.firstLevel}
                        contentWidth={contentWidth}
                    />

                    <div className="tw-relative" style={{ height: bodyHeight }}>
                        {/* Above the grey category rows so their column lines and breaks show; pills and source bands sit above it. */}
                        <div className="tw-absolute tw-top-0 tw-bottom-0 tw-z-[1] tw-pointer-events-none" style={{ left: LEFT_W, width: layout.width }}>
                            {visibleColumns.map((column, index) => {
                                const previous = index === 0 ? layout.columns[firstColumn - 1] : visibleColumns[index - 1];
                                const major = !previous || previous.group !== column.group;

                                return (
                                    <Fragment key={column.start}>
                                        <div
                                            className={classNames(
                                                'tw-absolute tw-top-0 tw-bottom-0 tw-border-s',
                                                major ? 'tw-border-charcoal-25' : 'tw-border-grey',
                                            )}
                                            style={{ left: column.x }}
                                        />
                                        {column.gapAfter && (
                                            <div
                                                className={classNames('tw-absolute tw-top-0 tw-bottom-0', HATCH_LIGHT)}
                                                style={{ left: column.x + column.w, width: GAP_W }}
                                            />
                                        )}
                                    </Fragment>
                                );
                            })}
                        </div>

                        {visibleRows.map((row) => (
                            <CalendarRow
                                key={row.key}
                                row={row}
                                pillMode={pillMode}
                                windowLeft={windowLeft}
                                windowRight={windowRight}
                                onToggle={toggleCollapsed}
                                onOpen={openMilestone}
                                onHover={showTooltip}
                                onLeave={hideTooltip}
                            />
                        ))}

                        {todayX !== null && (
                            <div
                                className="tw-absolute tw-top-0 tw-bottom-0 tw-z-10 tw-border-s-2 tw-border-dashed tw-border-blue tw-pointer-events-none"
                                style={{ left: LEFT_W + todayX }}
                            >
                                <span className="tw-sticky tw-top-14 tw-ms-1.5 tw-inline-block tw-mt-1.5 tw-rounded-full tw-bg-blue tw-px-2 tw-py-0.5 tw-text-2xs tw-font-semibold tw-text-white">
                                    {$t('Today')}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {tooltip && (
                <div
                    className="tw-fixed tw-z-100 tw-max-w-[280px] tw-rounded-md tw-bg-charcoal tw-px-3 tw-py-2 tw-text-xs tw-leading-snug tw-text-white tw-pointer-events-none"
                    style={{ left: tooltip.left, top: tooltip.top }}
                    role="tooltip"
                >
                    <div className="tw-font-semibold">{tooltip.milestone.name}</div>
                    <div className="tw-opacity-80">
                        {formatDateShort(tooltip.milestone.date)}
                        {tooltip.milestone.key && ` · ${$t('Key milestone')}`}
                        {tooltip.milestone.complete && ` · ${$t('Complete')}`}
                        {hasFloat(tooltip.milestone) && ` · ${$t('Float')}: ${floatDaysLabel(tooltip.milestone.float)}`}
                    </div>
                    {tooltip.milestone.owner_name && (
                        <div className="tw-opacity-80">
                            {$t('Owner')}: {tooltip.milestone.owner_name}
                        </div>
                    )}
                </div>
            )}

            <MilestoneDetailModal modal={milestoneModal} milestone={selectedMilestone} detailsUrl={milestoneUrl} onClose={hideMilestoneModal} />
        </Fragment>
    );
};

const CalendarHeader = memo(function CalendarHeader({ layout, visibleColumns, scrollLeft, firstLevel, contentWidth }) {
    // Group labels start at their first column; the group already under the left edge pins there
    // so the month/year stays readable in a long Days view.
    const groupLabels = [];
    visibleColumns.forEach((column, index) => {
        if (!column.group) {
            return;
        }

        const previous = groupLabels[groupLabels.length - 1];

        if (previous && previous.label === column.group) {
            previous.end = column.x + column.w;

            return;
        }

        const groupStartsEarlier =
            index === 0 && layout.columns.indexOf(column) > 0 && layout.columns[layout.columns.indexOf(column) - 1].group === column.group;

        groupLabels.push({ label: column.group, x: groupStartsEarlier ? -Infinity : column.x, end: column.x + column.w });
    });

    return (
        <div className="tw-sticky tw-top-0 tw-z-30 tw-flex tw-bg-white tw-border-b tw-border-charcoal-25" style={{ width: contentWidth, height: HEADER_H }}>
            <div
                className="tw-sticky tw-start-0 tw-z-40 tw-flex tw-items-end tw-shrink-0 tw-px-3.5 tw-pb-2 tw-bg-charcoal-15 tw-border-e tw-border-charcoal-25 tw-text-xs tw-font-semibold tw-text-charcoal"
                style={{ width: LEFT_W }}
            >
                <span dir="auto">{firstLevel ? $t('Source / category') : $t('Source / category / sub-category')}</span>
            </div>
            <div className="tw-relative tw-shrink-0 tw-bg-blue-10" style={{ width: layout.width, height: HEADER_H }}>
                {groupLabels.map((group) => {
                    const left = Math.max(group.x, Math.min(scrollLeft, group.end - 60));

                    return (
                        <div
                            key={`${group.label}-${group.end}`}
                            className="tw-absolute tw-top-0 tw-px-2 tw-pt-1 tw-text-2xs tw-font-semibold tw-text-charcoal-75 tw-whitespace-nowrap tw-border-s tw-border-blue-25"
                            style={{ left, height: HEADER_GROUP_H }}
                        >
                            {group.label}
                        </div>
                    );
                })}
                {visibleColumns.map((column) => (
                    <Fragment key={column.start}>
                        <div
                            className={classNames(
                                'tw-absolute tw-overflow-hidden tw-whitespace-nowrap tw-border-s tw-border-blue-25 tw-text-xs tw-font-semibold tw-text-charcoal',
                                { 'tw-bg-blue-25': column.isToday },
                            )}
                            style={
                                column.group
                                    ? { left: column.x, width: column.w, top: HEADER_GROUP_H, height: HEADER_PERIOD_H }
                                    : { left: column.x, width: column.w, top: 0, height: HEADER_H }
                            }
                        >
                            {/* Centred in the part not under the frozen column, until it is pushed out with the column. */}
                            <span
                                className="tw-absolute tw-inset-y-0 tw-end-0 tw-flex tw-items-center tw-justify-center"
                                style={{ left: Math.max(0, Math.min(scrollLeft - column.x, column.w - STICKY_PERIOD_LABEL_W)) }}
                            >
                                {column.label}
                            </span>
                        </div>
                        {column.gapAfter && (
                            <div
                                className={classNames('tw-absolute', HATCH)}
                                style={{ left: column.x + column.w, width: GAP_W, top: HEADER_GROUP_H, height: HEADER_PERIOD_H }}
                                title={$t('Empty periods hidden')}
                            />
                        )}
                    </Fragment>
                ))}
            </div>
        </div>
    );
});

const CalendarRow = memo(function CalendarRow({ row, pillMode, windowLeft, windowRight, onToggle, onOpen, onHover, onLeave }) {
    const isSource = row.type === 'source';
    const isCategory = row.type === 'category';
    const Chevron = row.collapsed ? ChevronRightIcon : ChevronDownIcon;

    const label = (
        <Fragment>
            {row.collapsible && <Chevron className="tw-h-2 tw-w-2 tw-shrink-0" aria-hidden="true" />}
            <span dir="auto" className="tw-truncate">
                {row.name}
            </span>
            {row.counts && (
                <span className={classNames('tw-ms-auto tw-shrink-0 tw-text-2xs tw-font-normal', isSource ? 'tw-text-charcoal-25' : 'tw-text-charcoal-50')}>
                    {countsLabel(row.counts)}
                </span>
            )}
        </Fragment>
    );

    const labelClassName = classNames('tw-flex tw-items-center tw-gap-2 tw-w-full tw-h-full tw-text-start', {
        'tw-ps-3 tw-pe-3.5 tw-text-sm tw-font-semibold': isSource,
        'tw-ps-4 tw-pe-3.5 tw-text-0.5sm tw-font-semibold': isCategory,
        'tw-ps-[34px] tw-pe-3.5 tw-text-0.5sm tw-text-charcoal-75': row.type === 'subcategory',
    });

    return (
        <div
            className={classNames('tw-absolute tw-start-0 tw-flex tw-w-full tw-border-b', {
                'tw-z-[2] tw-bg-charcoal tw-text-white tw-border-charcoal': isSource,
                'tw-bg-charcoal-10 tw-border-grey': isCategory,
                'tw-border-grey': row.type === 'subcategory',
            })}
            style={{ top: row.top, height: row.height }}
        >
            <div
                className={classNames('tw-sticky tw-start-0 tw-z-20 tw-shrink-0 tw-border-e tw-border-charcoal-25', {
                    'tw-bg-charcoal': isSource,
                    'tw-bg-charcoal-10': isCategory,
                    'tw-bg-white': row.type === 'subcategory',
                })}
                style={{ width: LEFT_W, height: row.height }}
            >
                {row.collapsible ? (
                    <button
                        type="button"
                        className={labelClassName}
                        style={{ height: isSource ? row.height : CATEGORY_ROW_H }}
                        aria-expanded={!row.collapsed}
                        onClick={() => onToggle(row.key)}
                    >
                        {label}
                    </button>
                ) : (
                    <div className={labelClassName} style={{ height: isSource ? row.height : CATEGORY_ROW_H }}>
                        {label}
                    </div>
                )}
            </div>
            <div className="tw-relative tw-flex-1">
                {row.pills
                    .filter((pill) => pill.x + pill.w >= windowLeft && pill.x <= windowRight)
                    .map((pill) => (
                        <RoadmapPill key={pill.milestone.id} pill={pill} mode={pillMode} onOpen={onOpen} onHover={onHover} onLeave={onLeave} />
                    ))}
            </div>
        </div>
    );
});

const hasFloat = (milestone) => milestone.float !== undefined && milestone.float !== null;

/**
 * The pill's fill, border and text. While the critical path is on, every pill carries a float:
 * 0 is red, near-critical amber, and a key milestone is filled.
 */
const pillTone = (milestone) => {
    if (hasFloat(milestone)) {
        const critical = milestone.float === 0;

        if (milestone.key) {
            return critical ? 'tw-bg-red tw-border-red tw-text-white' : 'tw-bg-amber-500 tw-border-amber-500 tw-text-charcoal';
        }

        return critical ? 'tw-bg-red-10 tw-border-red tw-text-charcoal' : 'tw-bg-amber-50 tw-border-amber-500 tw-text-charcoal';
    }

    return classNames({
        'tw-bg-white tw-border-blue tw-text-charcoal': !milestone.key && !milestone.complete,
        'tw-bg-blue tw-border-blue tw-text-white': milestone.key && !milestone.complete,
        'tw-bg-green-25 tw-border-green tw-text-dark-green': !milestone.key && milestone.complete,
        'tw-bg-green tw-border-green tw-text-charcoal': milestone.key && milestone.complete,
    });
};

const dotTone = (milestone) => {
    if (hasFloat(milestone)) {
        return milestone.float === 0 ? 'tw-border-red' : 'tw-border-amber-500';
    }

    return milestone.complete ? 'tw-border-dark-green tw-bg-dark-green' : 'tw-border-blue';
};

const RoadmapPill = ({ pill, mode, onOpen, onHover, onLeave }) => {
    const { milestone } = pill;
    const compact = mode === 'compact';
    const date = formatDateShort(milestone.date);
    const float = hasFloat(milestone) ? floatDaysLabel(milestone.float) : null;

    const ariaLabel = [
        milestone.name,
        date,
        milestone.key ? $t('Key milestone') : null,
        milestone.complete ? $t('Complete') : null,
        float ? `${$t('Float')}: ${float}` : null,
    ]
        .filter(Boolean)
        .join(', ');

    return (
        <button
            type="button"
            aria-label={ariaLabel}
            onClick={() => onOpen(milestone)}
            onMouseEnter={(event) => onHover(milestone, event.currentTarget.getBoundingClientRect())}
            onMouseLeave={onLeave}
            onFocus={(event) => onHover(milestone, event.currentTarget.getBoundingClientRect())}
            onBlur={onLeave}
            className={classNames(
                'tw-absolute tw-z-[2] tw-flex tw-items-center tw-gap-1.5 tw-ps-2.5 tw-pe-3 tw-rounded-md tw-border tw-border-s-[3px] tw-font-medium tw-whitespace-nowrap tw-shadow-sm tw-overflow-hidden',
                'hover:tw-z-10 hover:tw-shadow-md focus-visible:tw-z-10 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-1 focus-visible:tw-outline-blue',
                compact ? 'tw-h-6 tw-text-xs' : 'tw-h-[30px] tw-text-0.5sm',
                pillTone(milestone),
            )}
            style={{ left: pill.x, top: pillTop(pill.lane), width: pill.w }}
        >
            {milestone.key ? (
                <StarFilledIcon className="tw-h-3 tw-w-3 tw-shrink-0" aria-hidden="true" />
            ) : (
                <span className={classNames('tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-border-[1.5px]', dotTone(milestone))} aria-hidden="true" />
            )}
            <span dir="auto" className="tw-min-w-0 tw-truncate">
                {milestone.name}
            </span>
            {!compact && <span className="tw-shrink-0 tw-text-2xs tw-font-light tw-opacity-75">{date}</span>}
            {float && <span className="tw-shrink-0 tw-text-2xs tw-font-semibold">{float}</span>}
        </button>
    );
};

export default RoadmapCalendar;
